'use client';

import { TypesenseEventDocument } from '@/types';
import { IconPlus } from '@tabler/icons-react';
import { format } from 'date-fns';
import { parseAsIsoDateTime, useQueryState } from 'nuqs';

import { Button } from '../button';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';

export function EventDates({ instances }: { instances: TypesenseEventDocument[] }) {
  const [searchDate, setSearchDate] = useQueryState(
    'date',
    parseAsIsoDateTime.withDefault(new Date(instances?.[0]?.dates * 1000)),
  );

  const handleDateChange = (date: number) => {
    setSearchDate(new Date(date));
  };

  // Find the index of the currently selected event
  const selectedIndex = instances.findIndex(
    (instance) => new Date(instance.dates * 1000).toISOString() === searchDate?.toISOString(),
  );

  // Determine how to split the events
  let beforeEvents: TypesenseEventDocument[] = [];
  let middleEvents: TypesenseEventDocument[] = [];
  let afterEvents: TypesenseEventDocument[] = [];

  if (selectedIndex !== -1) {
    // If the selected event is in the first position
    if (selectedIndex === 0) {
      middleEvents = instances.slice(0, Math.min(3, instances.length));
      afterEvents = instances.slice(3);
    }
    // If the selected event is in the last position
    else if (selectedIndex === instances.length - 1) {
      const startIndex = Math.max(0, instances.length - 3);
      beforeEvents = instances.slice(0, startIndex);
      middleEvents = instances.slice(startIndex);
    }
    // If the selected event is somewhere in the middle
    else {
      // Try to get one before and one after the selected event
      const startIndex = Math.max(0, selectedIndex - 1);
      const endIndex = Math.min(instances.length, selectedIndex + 2);

      beforeEvents = instances.slice(0, startIndex);
      middleEvents = instances.slice(startIndex, endIndex);
      afterEvents = instances.slice(endIndex);
    }
  } else {
    // Fallback if no event is selected
    middleEvents = instances.slice(0, Math.min(3, instances.length));
    afterEvents = instances.slice(3);
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {/* Dropdown for earlier events if any */}
        {Boolean(beforeEvents.length) && (
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline">
                <IconPlus className="size-4" />
                {beforeEvents.length} earlier
              </Button>
            </PopoverTrigger>

            <PopoverContent className="flex flex-col p-2">
              {beforeEvents.map((instance) => {
                const date = new Date(instance.dates * 1000);

                return (
                  <Button
                    key={instance.dates}
                    size="sm"
                    variant={date.toISOString() === searchDate?.toISOString() ? 'default' : 'link'}
                    className="justify-start"
                    onClick={() => handleDateChange(instance.dates * 1000)}
                  >
                    {format(instance.dates * 1000, 'EE do MMM')}
                  </Button>
                );
              })}
            </PopoverContent>
          </Popover>
        )}

        {/* Main visible events */}
        {middleEvents.map((instance) => {
          const date = new Date(instance.dates * 1000);

          return (
            <Button
              onClick={() => handleDateChange(instance.dates * 1000)}
              key={instance.dates}
              size="sm"
              variant={date.toISOString() === searchDate?.toISOString() ? 'secondary' : 'outline'}
            >
              {format(instance.dates * 1000, 'EE do MMM')}
            </Button>
          );
        })}

        {/* Dropdown for later events if any */}
        {Boolean(afterEvents.length) && (
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline">
                <IconPlus className="size-4" />
                {Math.min(afterEvents.length, 10)} later
              </Button>
            </PopoverTrigger>

            <PopoverContent className="flex flex-col p-2">
              {afterEvents.slice(0, 10).map((instance) => {
                const date = new Date(instance.dates * 1000);

                return (
                  <Button
                    key={instance.dates}
                    size="sm"
                    variant={date.toISOString() === searchDate?.toISOString() ? 'default' : 'link'}
                    className="justify-start"
                    onClick={() => handleDateChange(instance.dates * 1000)}
                  >
                    {format(instance.dates * 1000, 'EE do MMM')}
                  </Button>
                );
              })}
            </PopoverContent>
          </Popover>
        )}
      </div>
    </>
  );
}
